import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCircle2, Clock, CreditCard, DollarSign, Loader2, MailOpen, MailPlus, RefreshCw, Undo2, X, XCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import api from '../../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const POLL_INTERVAL_MS = 30_000;

const kindMeta = {
  initiated: { label: 'Initiated', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-rose-100 text-rose-800 border-rose-200', icon: XCircle },
  refunded: { label: 'Refunded', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Undo2 },
};

const sourceLabel = {
  stripe_checkout: 'Stripe',
  stripe_direct: 'Stripe',
  offline: 'Offline',
  refund: 'Refund',
};

const formatAmount = (n, currency = 'usd') => {
  if (n == null) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: currency.toUpperCase() });
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return format(parseISO(iso), 'MMM d, h:mm a');
  } catch {
    return '';
  }
};

const EventRow = ({ event, onToggleRead, onDismiss, onUndismiss, busy }) => {
  const meta = kindMeta[event.kind] || kindMeta.initiated;
  const Icon = meta.icon;
  const isUnread = event.admin_status === 'unread';
  const isDismissed = event.admin_status === 'dismissed';

  return (
    <div
      data-testid={`payment-notif-row-${event.event_key}`}
      className={`group flex items-start gap-3 p-3 border-b last:border-b-0 transition ${
        isUnread ? 'bg-blue-50/40' : 'bg-white'
      }`}
    >
      <div className={`mt-0.5 shrink-0 p-1.5 rounded-md border ${meta.color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm">
            {formatAmount(event.amount, event.currency)}
          </span>
          <Badge variant="outline" className={`text-[10px] ${meta.color}`}>
            Payment {meta.label}
          </Badge>
          {event.is_ach && (
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
              ACH · 3–5d
            </Badge>
          )}
          <span className="text-[10px] text-slate-500">
            {sourceLabel[event.source] || event.source}
          </span>
        </div>
        <div className="text-xs text-slate-600 mt-0.5 truncate">
          {event.user_name || event.user_email || 'Unknown customer'}
          {event.entity_type && <span className="text-slate-400"> · {event.entity_type.replace(/_/g, ' ')}</span>}
        </div>
        {event.kind === 'initiated' && event.is_ach && (
          <div className="text-[11px] text-amber-700 mt-1">
            Bank transfer in progress — will update when Stripe confirms.
          </div>
        )}
        <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-1">
          <span>{formatTime(event.created_at)}</span>
          {isDismissed && <span className="italic">· dismissed</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0 opacity-70 group-hover:opacity-100 transition">
        {!isDismissed && (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={busy}
              onClick={() => onToggleRead(event)}
              title={isUnread ? 'Mark as read' : 'Mark as unread'}
              data-testid={`payment-notif-toggle-read-${event.event_key}`}
            >
              {isUnread ? <MailOpen className="h-3.5 w-3.5" /> : <MailPlus className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-slate-500 hover:text-rose-600"
              disabled={busy}
              onClick={() => onDismiss(event)}
              title="Dismiss"
              data-testid={`payment-notif-dismiss-${event.event_key}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
        {isDismissed && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={busy}
            onClick={() => onUndismiss(event)}
            title="Restore"
            data-testid={`payment-notif-undismiss-${event.event_key}`}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const FeedBody = ({ events, loading, busyKey, onToggleRead, onDismiss, onUndismiss, onMarkAllRead, showDismissed, onToggleShowDismissed, onRefresh }) => {
  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        <DollarSign className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        No recent payment activity.
      </div>
    );
  }
  const unreadCount = events.filter((e) => e.admin_status === 'unread').length;
  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 text-xs text-slate-600">
        <span>
          {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={onRefresh}
            title="Refresh"
            data-testid="payment-notif-refresh"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={onMarkAllRead}
              data-testid="payment-notif-mark-all-read"
            >
              Mark all read
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={onToggleShowDismissed}
            data-testid="payment-notif-toggle-show-dismissed"
          >
            {showDismissed ? 'Hide dismissed' : 'Show dismissed'}
          </Button>
        </div>
      </div>
      <div
        className="overflow-y-auto"
        style={{ maxHeight: 'min(60vh, 480px)' }}
        data-testid="payment-notif-scroll-area"
      >
        {events.map((e) => (
          <EventRow
            key={e.event_key}
            event={e}
            busy={busyKey === e.event_key}
            onToggleRead={onToggleRead}
            onDismiss={onDismiss}
            onUndismiss={onUndismiss}
          />
        ))}
      </div>
      <div className="px-3 py-2 border-t text-[11px] text-slate-400 text-center bg-slate-50">
        Showing the last {events.length} payment events. For full history, see the Recent Payments page.
      </div>
    </>
  );
};

const AdminPaymentNotifications = ({ variant = 'bell' }) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [unreadBadge, setUnreadBadge] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const pollRef = useRef(null);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payment-notifications?include_dismissed=${showDismissed}`);
      setEvents(data?.events || []);
      setUnreadBadge(data?.unread_count || 0);
    } catch {
      // Silent — the bell just won't update; avoid spamming toasts on poll.
    } finally {
      setLoading(false);
    }
  }, [showDismissed]);

  const fetchUnreadOnly = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/payment-notifications/unread-count');
      setUnreadBadge(data?.unread_count || 0);
    } catch {
      // Silent
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (!open) fetchUnreadOnly();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, fetchUnreadOnly]);

  const handleToggleRead = async (event) => {
    setBusyKey(event.event_key);
    const next = event.admin_status === 'unread' ? 'read' : 'unread';
    try {
      await api.post(`/admin/payment-notifications/${next}`, { event_key: event.event_key });
      setEvents((prev) =>
        prev.map((e) => (e.event_key === event.event_key ? { ...e, admin_status: next } : e))
      );
      setUnreadBadge((b) => Math.max(0, b + (next === 'read' ? -1 : 1)));
    } catch {
      toast.error('Could not update notification status');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDismiss = async (event) => {
    setBusyKey(event.event_key);
    try {
      await api.post('/admin/payment-notifications/dismiss', { event_key: event.event_key });
      if (showDismissed) {
        setEvents((prev) =>
          prev.map((e) => (e.event_key === event.event_key ? { ...e, admin_status: 'dismissed' } : e))
        );
      } else {
        setEvents((prev) => prev.filter((e) => e.event_key !== event.event_key));
      }
      if (event.admin_status === 'unread') setUnreadBadge((b) => Math.max(0, b - 1));
    } catch {
      toast.error('Could not dismiss notification');
    } finally {
      setBusyKey(null);
    }
  };

  const handleUndismiss = async (event) => {
    setBusyKey(event.event_key);
    try {
      await api.post('/admin/payment-notifications/undismiss', { event_key: event.event_key });
      setEvents((prev) =>
        prev.map((e) => (e.event_key === event.event_key ? { ...e, admin_status: 'unread' } : e))
      );
      setUnreadBadge((b) => b + 1);
    } catch {
      toast.error('Could not restore notification');
    } finally {
      setBusyKey(null);
    }
  };

  const handleMarkAllRead = async () => {
    const keys = events.filter((e) => e.admin_status === 'unread').map((e) => e.event_key);
    if (keys.length === 0) return;
    try {
      await api.post('/admin/payment-notifications/read-bulk', { event_keys: keys });
      setEvents((prev) =>
        prev.map((e) => (e.admin_status === 'unread' ? { ...e, admin_status: 'read' } : e))
      );
      setUnreadBadge(0);
      toast.success(`Marked ${keys.length} as read`);
    } catch {
      toast.error('Could not mark all as read');
    }
  };

  const handleToggleShowDismissed = () => setShowDismissed((v) => !v);

  // ------------------------------ Variants ----------------------------

  if (variant === 'card') {
    return (
      <Card data-testid="payment-notif-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4 text-blue-600" />
              Recent Payment Activity
              {unreadBadge > 0 && (
                <Badge className="bg-blue-600 text-white text-[10px]">{unreadBadge} new</Badge>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 border-t">
          <FeedBody
            events={events}
            loading={loading}
            busyKey={busyKey}
            onToggleRead={handleToggleRead}
            onDismiss={handleDismiss}
            onUndismiss={handleUndismiss}
            onMarkAllRead={handleMarkAllRead}
            showDismissed={showDismissed}
            onToggleShowDismissed={handleToggleShowDismissed}
            onRefresh={fetchFeed}
          />
        </CardContent>
      </Card>
    );
  }

  // Default bell variant
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) fetchFeed();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative"
          data-testid="payment-notif-bell"
          title="Recent payment activity"
        >
          <Bell className="h-4 w-4 text-slate-600" />
          {unreadBadge > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center px-1"
              data-testid="payment-notif-unread-badge"
            >
              {unreadBadge > 99 ? '99+' : unreadBadge}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[420px] p-0"
        data-testid="payment-notif-popover"
      >
        <div className="p-3 border-b bg-slate-50">
          <div className="font-semibold text-sm flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-blue-600" />
            Recent Payments
          </div>
        </div>
        <FeedBody
          events={events}
          loading={loading}
          busyKey={busyKey}
          onToggleRead={handleToggleRead}
          onDismiss={handleDismiss}
          onUndismiss={handleUndismiss}
          onMarkAllRead={handleMarkAllRead}
          showDismissed={showDismissed}
          onToggleShowDismissed={handleToggleShowDismissed}
          onRefresh={fetchFeed}
        />
      </PopoverContent>
    </Popover>
  );
};

export default AdminPaymentNotifications;
